<template>
  <div class="filter-editor" :class="accentClass">
    <div class="filter-list">
      <section class="filter-group">
        <article
          v-for="config in primaryFilters"
          :key="`${config.mode}-${config.id}`"
          :ref="cardRefFor(config)"
          class="filter-condition"
        >
          <label class="condition-label">
            <span>{{ config.text }}</span>
            <InfoTooltip v-if="config.tooltip" :text="config.tooltip" />
          </label>
          <div class="condition-input">
            <component
              :is="config.filter.UI"
              :key="editorSessionKey"
              :ref="filterRefFor(config)"
              :mode="config.mode"
              :types="types"
              :site-id="draftSiteId"
              @update:site-id="draftSiteId = $event"
            />
          </div>
          <LISAUIButton
            class="negated-control"
            size="sm"
            variant="secondary"
            :outlined="!stateFor(config).negated"
            :icon="stateFor(config).negated ? 'fas fa-not-equal' : 'fas fa-equals'"
            :aria-pressed="stateFor(config).negated"
            :aria-label="t('components.eventAction.FilterList.setFilterNegated')"
            :tooltip="t('components.eventAction.FilterList.setFilterNegated')"
            @click="stateFor(config).negated = !stateFor(config).negated"
          />
        </article>
      </section>

      <details
        v-if="collapsedFilters.length > 0"
        ref="additionalFilters"
        class="additional-filters"
      >
        <summary>
          <span>{{ t("global.joinList.more") }}</span>
          <i class="fas fa-chevron-down" aria-hidden="true" />
        </summary>
        <section class="filter-group">
          <article
            v-for="config in collapsedFilters"
            :key="`${config.mode}-${config.id}`"
            :ref="cardRefFor(config)"
            class="filter-condition"
          >
            <label class="condition-label">
              <span>{{ config.text }}</span>
              <InfoTooltip v-if="config.tooltip" :text="config.tooltip" />
            </label>
            <div class="condition-input">
              <component
                :is="config.filter.UI"
                :key="editorSessionKey"
                :ref="filterRefFor(config)"
                :mode="config.mode"
                :types="types"
                :site-id="draftSiteId"
                @update:site-id="draftSiteId = $event"
              />
            </div>
            <LISAUIButton
              class="negated-control"
              size="sm"
              variant="secondary"
              :outlined="!stateFor(config).negated"
              :icon="stateFor(config).negated ? 'fas fa-not-equal' : 'fas fa-equals'"
              :aria-pressed="stateFor(config).negated"
              :aria-label="t('components.eventAction.FilterList.setFilterNegated')"
              :tooltip="t('components.eventAction.FilterList.setFilterNegated')"
              @click="stateFor(config).negated = !stateFor(config).negated"
            />
          </article>
        </section>
      </details>
    </div>

    <footer class="editor-footer">
      <div class="logic-options">
        <span class="logic-options__label">{{
          t("components.eventAction.FilterList.logicLabel")
        }}</span>
        <InfoTooltip>
          <div>AND: {{ t("components.eventAction.FilterList.allConditions") }}</div>
          <div>OR: {{ t("components.eventAction.FilterList.atLeastOne") }}</div>
        </InfoTooltip>
        <label>
          <input v-model="selectedFilterType" type="radio" :value="EABFilterType.AND" />
          <span>AND</span>
        </label>
        <label>
          <input v-model="selectedFilterType" type="radio" :value="EABFilterType.OR" />
          <span>OR</span>
        </label>
      </div>
      <div class="footer-actions">
        <LISAUIButton
          class="footer-action"
          size="sm"
          variant="neutral"
          outlined
          icon="fas fa-xmark"
          :aria-label="t('global.cancel')"
          @click="$emit('close')"
        >
          <span class="footer-action__label">{{ t("global.cancel") }}</span>
        </LISAUIButton>
        <LISAUIButton
          size="sm"
          variant="primary"
          class="footer-action"
          icon="fas fa-filter"
          :aria-label="t('components.eventAction.FilterList.setFilter')"
          @click="applyFilters"
        >
          <span class="footer-action__label">
            {{ t("components.eventAction.FilterList.setFilter") }}
          </span>
        </LISAUIButton>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import {
  computed,
  nextTick,
  reactive,
  ref,
  type ComponentPublicInstance,
  type PropType,
} from "vue";
import { LISAButton as LISAUIButton } from "@fi-solutions/lisa-ui";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import { EABFilterType } from "@/graphql";
import { Filter, type FilterConfig, type FilterEventActionTypes } from "./FilterTypes";
import SiteFilter from "./filterImplementations/SiteFilter";

type FilterUIInstance = {
  getFilter(): Filter | Filter[] | null | undefined;
  applyFilter(filter: Filter): void;
};

const props = defineProps({
  filterConfig: {
    required: true,
    type: Array as PropType<FilterConfig[]>,
  },
  types: {
    type: Array as PropType<FilterEventActionTypes[]>,
    default: () => [],
  },
  title: {
    type: String,
    required: true,
  },
  accent: {
    type: String as PropType<"event" | "action">,
    required: true,
  },
  eventFilters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  actionFilters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const emit = defineEmits<{
  apply: [filters: Filter[], type: EABFilterType];
  close: [];
}>();

const filterInstances = new Map<FilterConfig, FilterUIInstance>();
const cardElements = new Map<FilterConfig, HTMLElement>();
const conditionState = reactive(
  new Map<FilterConfig, { negated: boolean }>(
    props.filterConfig.map((config) => [config, { negated: false }]),
  ),
);
const selectedFilterType = ref(EABFilterType.AND);
const editorSessionKey = ref(0);
const draftSiteId = ref<number>();
const additionalFilters = ref<HTMLDetailsElement>();
const accentClass = computed(() => `${props.accent}-editor`);
const primaryFilters = computed(() => props.filterConfig.filter((config) => !config.collapsed));
const collapsedFilters = computed(() => props.filterConfig.filter((config) => config.collapsed));

function stateFor(config: FilterConfig) {
  let state = conditionState.get(config);
  if (state == null) {
    state = reactive({ negated: false });
    conditionState.set(config, state);
  }
  return state;
}

function setFilterRef(config: FilterConfig, instance: unknown) {
  if (instance == null) {
    filterInstances.delete(config);
    return;
  }
  filterInstances.set(config, instance as FilterUIInstance);
}

function filterRefFor(config: FilterConfig) {
  return (instance: unknown) => setFilterRef(config, instance);
}

function setCardRef(config: FilterConfig, element: Element | ComponentPublicInstance | null) {
  if (element instanceof HTMLElement) {
    cardElements.set(config, element);
  } else {
    cardElements.delete(config);
  }
}

function cardRefFor(config: FilterConfig) {
  return (element: Element | ComponentPublicInstance | null) => setCardRef(config, element);
}

function configFor(filter: Filter) {
  return props.filterConfig.find((config) => filter instanceof config.filter);
}

async function initialize(filters: Filter[], type: EABFilterType, focusFilter?: Filter) {
  draftSiteId.value =
    (filters.find((filter) => filter instanceof SiteFilter) as SiteFilter | undefined)?.siteId ??
    props.siteId;
  conditionState.forEach((state) => {
    state.negated = false;
  });
  selectedFilterType.value = type;
  editorSessionKey.value += 1;
  await nextTick();
  if (additionalFilters.value != null) {
    additionalFilters.value.open = false;
  }
  filters.forEach((filter) => {
    const config = configFor(filter);
    if (config == null) {
      return;
    }
    stateFor(config).negated = filter.isNegated ?? false;
  });
  filters.forEach((filter) => {
    const config = configFor(filter);
    if (config == null) {
      return;
    }
    filterInstances.get(config)?.applyFilter(filter);
  });
  if (focusFilter != null) {
    const config = configFor(focusFilter);
    if (config != null) {
      if (config.collapsed && additionalFilters.value != null) {
        additionalFilters.value.open = true;
      }
      await nextTick();
      cardElements.get(config)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
}

function reset() {
  conditionState.forEach((state) => {
    state.negated = false;
  });
  selectedFilterType.value = EABFilterType.AND;
  editorSessionKey.value += 1;
}

function collectFilters() {
  return props.filterConfig.flatMap((config) => {
    const state = stateFor(config);
    const filter = filterInstances.get(config)?.getFilter();
    if (filter == null) {
      return [];
    }
    const filters = Array.isArray(filter) ? filter : [filter];
    filters.forEach((entry) => {
      entry.isNegated = state.negated;
    });
    return filters;
  });
}

function applyFilters() {
  const filters = collectFilters();
  emit("apply", filters, selectedFilterType.value);
}

defineExpose({
  initialize,
  reset,
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.filter-editor {
  --filter-control-font-size: var(--eab-text-sm, 0.875rem);

  display: flex;
  max-height: min(45rem, calc(100dvh - 1rem));
  flex-direction: column;
  overflow: hidden;
  background: var(--p-surface-0);
  font-size: var(--filter-control-font-size);
}

.editor-footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem 1rem;
}

.filter-list {
  min-height: 0;
  flex: 1;
  overscroll-behavior: contain;
  overflow-x: hidden;
  overflow-y: auto;
}

.filter-group {
  padding: 0.65rem 1rem;
}

.filter-condition {
  display: grid;
  min-width: 0;
  align-items: center;
  grid-template-columns: minmax(5rem, 7rem) minmax(0, 1fr) 2.5rem;
  gap: 0.5rem;
  padding: 0.35rem 0;
}

.condition-label {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.25rem;
  color: var(--lisa-primary);
  font-weight: 600;

  > span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
}

.logic-options,
.logic-options label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.logic-options label {
  cursor: pointer;
}

.logic-options input[type="radio"] {
  margin: 0;
}

.logic-options__label {
  color: var(--lisa-primary);
  font-weight: 600;
}

.condition-input {
  width: 100%;
  min-width: 0;
}

.negated-control {
  align-self: start;
  width: 2.5rem;
  min-width: 2.5rem;
  height: 2.5rem;
  min-height: 2.5rem;
}

.additional-filters {
  border-top: 1px solid var(--lisa-secondary);
}

.additional-filters summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  color: var(--lisa-muted-foreground);
  cursor: pointer;
  font-weight: 700;
  list-style: none;
}

.additional-filters[open] summary i {
  transform: rotate(180deg);
}

.editor-footer {
  gap: 0.75rem 1rem;
  border-top: 1px solid var(--lisa-secondary);
}

.footer-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

:deep(.p-inputtext),
:deep(.p-select),
:deep(.p-multiselect),
:deep(.p-inputnumber),
:deep(.p-inputnumber-input),
:deep(.dp__main) {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

:deep(.lisa-input__control),
:deep(.lisa-select__control > *),
:deep(.lisa-select__control .lisa-chip),
:deep(.lisa-button),
:deep(.p-inputtext),
:deep(.p-select-label),
:deep(.p-multiselect-label),
:deep(.p-button) {
  font-size: var(--filter-control-font-size) !important;
}

:deep(.p-inputtext:focus),
:deep(.p-select.p-focus),
:deep(.p-multiselect.p-focus),
:deep(.p-inputnumber-input:focus) {
  border-color: var(--lisa-primary) !important;
  box-shadow: 0 0 0 1px color-mix(in oklab, var(--lisa-primary), transparent 65%) !important;
}

@media (max-width: 47.5rem) {
  .filter-condition {
    grid-template-columns: minmax(0, 1fr) 2.5rem;
  }

  .condition-label {
    grid-column: 1 / -1;
  }

  .footer-action {
    width: 2.5rem;
    min-width: 2.5rem;
  }

  .footer-action__label {
    display: none;
  }
}
</style>
